import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { SpeechRateAnalysis } from '../services/speechRateService';
import { speechRateApi } from '../services/apiService';

// Enhanced deduplication function that catches more types of duplicates
function isDuplicateTranscript(newText: string, existingText: string, timeDiff: number): boolean {
  const normalize = (text: string) => text.toLowerCase().trim().replace(/\s+/g, ' ');
  const normalizedNew = normalize(newText);
  const normalizedExisting = normalize(existingText);
  
  // Check for exact duplicates
  if (normalizedNew === normalizedExisting) {
    return true;
  }
  
  // Check for partial duplicates (one is contained within the other)
  if (normalizedNew.includes(normalizedExisting) || normalizedExisting.includes(normalizedNew)) {
    // Only consider it a duplicate if the time difference is small (within 3 seconds)
    return timeDiff < 3000;
  }
  
  // Check for very similar texts using a simple similarity check
  const words1 = normalizedNew.split(' ').filter(word => word.length > 2);
  const words2 = normalizedExisting.split(' ').filter(word => word.length > 2);
  
  if (words1.length > 0 && words2.length > 0) {
    const commonWords = words1.filter(word => words2.includes(word));
    const similarity = commonWords.length / Math.max(words1.length, words2.length);
    
    // If similarity is high (>80%) and time difference is small, consider it a duplicate
    if (similarity > 0.8 && timeDiff < 5000) {
      return true;
    }
  }
  
  return false;
}

export interface EntryFeedback {
  rating: 'up' | 'down';
  reason?: string;
  submittedAt: string;
}

export interface ConversationEntry {
  id: string;
  question: string;
  answer?: string;
  stored_answer?: string;  // Answer from vector store database
  category: string;
  relevance_score: number;
  timestamp: string;
  status: 'pending' | 'completed' | 'error';
  error?: string;
  isNew?: boolean;
  feedback?: EntryFeedback;
}

export interface TranscriptEntry {
  id: string;
  text: string;
  speaker: 'customer' | 'salesperson';
  // Individual customer identity (multi-party attribution).
  participantId?: string;
  speakerName?: string;
  timestamp: string;
}

export interface Participant {
  participantId: string;
  name?: string;
  role: 'customer' | 'salesperson';
  source?: string;
  externalId?: string;
}

export interface SpeechRateAlert {
  total_words: number;
  max_words: number;
  time_window: number;
  words_per_minute: number;
  timestamp: string;
}

// Async thunk for fetching speech rate settings (read-only from frontend)
export const fetchSpeechRateSettings = createAsyncThunk(
  'conversation/fetchSpeechRateSettings',
  async () => {
    const response = await speechRateApi.getSettings();
    return response.data;
  }
);

interface ConversationState {
  entries: ConversationEntry[];
  transcripts: TranscriptEntry[];
  speechRateSettings: {
    enabled: boolean;
    time_window: number;
    max_words: number;
  };
  currentSpeechRate: {
    words_per_minute: number;
    is_too_fast: boolean;
  };
  lastSpeechRateAlert?: SpeechRateAlert;
  stats: {
    questionsDetected: number;
    pendingAnswers: number;
  };
  sessionSaved: boolean;  // Track if current session has been saved
  isSalespersonHost: boolean;  // Track if salesperson is the host or participant
  participants: Participant[];  // Multi-party roster (1 salesperson + N customers)
  salespersonResolved: boolean; // false → prompt "which participant is you?"
  confirmedParticipantIds: string[]; // participantIds the salesperson has confirmed
}

const initialState: ConversationState = {
  entries: [],
  transcripts: [],
  speechRateSettings: {
    enabled: true,
    time_window: 10,
    max_words: 20,
  },
  currentSpeechRate: {
    words_per_minute: 0,
    is_too_fast: false,
  },
  stats: {
    questionsDetected: 0,
    pendingAnswers: 0,
  },
  sessionSaved: false,
  isSalespersonHost: true,  // Default: salesperson is the host
  participants: [],
  salespersonResolved: true,
  confirmedParticipantIds: [],
};

const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    addQuestion: (state, action: PayloadAction<{
      question: string;
      category: string;
      relevance_score: number;
      timestamp: string;
    }>) => {
      const id = `${action.payload.question}_${Date.now()}`;
      const entry: ConversationEntry = {
        id,
        ...action.payload,
        status: 'pending',
        isNew: true,
      };
      state.entries.push(entry);
      state.stats.questionsDetected += 1;
      state.stats.pendingAnswers += 1;
    },
    
    markEntryAsOld: (state, action: PayloadAction<string>) => {
      const entry = state.entries.find(e => e.id === action.payload);
      if (entry) {
        entry.isNew = false;
      }
    },
    
    addStoredAnswer: (state, action: PayloadAction<{
      question: string;
      stored_answer: string;
      timestamp: string;
    }>) => {
      const entry = state.entries.find(
        e => e.question === action.payload.question && e.status === 'pending'
      );
      if (entry) {
        entry.stored_answer = action.payload.stored_answer;
        // Don't change status yet - wait for AI answer
      }
    },

    addAnswer: (state, action: PayloadAction<{
      question: string;
      answer: string;
      timestamp: string;
    }>) => {
      const entry = state.entries.find(
        e => e.question === action.payload.question && e.status === 'pending'
      );
      if (entry) {
        entry.answer = action.payload.answer;
        entry.status = 'completed';
        state.stats.pendingAnswers -= 1;
      }
    },
    
    markAnswerError: (state, action: PayloadAction<{
      question: string;
      error: string;
      timestamp: string;
    }>) => {
      const entry = state.entries.find(
        e => e.question === action.payload.question && e.status === 'pending'
      );
      if (entry) {
        entry.error = action.payload.error;
        entry.status = 'error';
        state.stats.pendingAnswers -= 1;
      }
    },
    
    
    updateCurrentSpeechRate: (state, action: PayloadAction<{
      words_per_minute: number;
      is_too_fast: boolean;
    }>) => {
      state.currentSpeechRate = action.payload;
    },
    
    updateSpeechRateAnalysis: (state, action: PayloadAction<SpeechRateAnalysis>) => {
      state.currentSpeechRate = {
        words_per_minute: action.payload.words_per_minute,
        is_too_fast: action.payload.is_too_fast,
      };
      
      // Create alert if speaking too fast
      if (action.payload.is_too_fast) {
        state.lastSpeechRateAlert = {
          total_words: action.payload.total_words,
          max_words: action.payload.max_words,
          time_window: action.payload.time_window,
          words_per_minute: action.payload.words_per_minute,
          timestamp: new Date().toISOString(),
        };
      }
    },
    
    addSpeechRateAlert: (state, action: PayloadAction<SpeechRateAlert>) => {
      state.lastSpeechRateAlert = action.payload;
      state.currentSpeechRate.is_too_fast = true;
    },
    
    clearSpeechRateAlert: (state) => {
      state.currentSpeechRate.is_too_fast = false;
      state.lastSpeechRateAlert = undefined;
    },
    
    addTranscript: (state, action: PayloadAction<{
      text: string;
      speaker: 'customer' | 'salesperson';
      timestamp: string;
      participantId?: string;
      speakerName?: string;
    }>) => {
      const newTranscript = action.payload;
      
      // Enhanced deduplication: check last 5 transcripts from the same speaker
      const now = new Date(newTranscript.timestamp).getTime();
      const recentTranscripts = state.transcripts.slice(-5); // Check last 5 entries
      
      const isDuplicateRecently = recentTranscripts.some(existing => {
        if (existing.speaker === newTranscript.speaker) {
          const timeDiff = Math.abs(now - new Date(existing.timestamp).getTime());
          // Use enhanced deduplication logic
          return isDuplicateTranscript(existing.text, newTranscript.text, timeDiff);
        }
        return false;
      });
      
      if (!isDuplicateRecently) {
        const id = `${action.payload.speaker}_${Date.now()}`;
        const transcript: TranscriptEntry = {
          id,
          ...action.payload,
        };
        state.transcripts.push(transcript);
        
        // Keep only the last 50 transcripts to prevent memory issues
        if (state.transcripts.length > 50) {
          state.transcripts = state.transcripts.slice(-50);
        }
        
        console.log('✅ Added new transcript:', newTranscript.text.slice(0, 50));
      } else {
        // console.log('🔄 Redux: Filtered duplicate transcript:', newTranscript.text.slice(0, 50));
      }
    },
    
    clearConversation: (state) => {
      state.entries = [];
      state.transcripts = [];
      state.stats.questionsDetected = 0;
      state.stats.pendingAnswers = 0;
      state.sessionSaved = false;  // Reset saved status when clearing
      // Reset multi-party roster for the next session.
      state.participants = [];
      state.confirmedParticipantIds = [];
      state.salespersonResolved = true;
    },
    
    updateQuestion: (state, action: PayloadAction<{
      originalQuestion: string;
      editedQuestion: string;
      answer: string;
    }>) => {
      const entry = state.entries.find(e => e.question === action.payload.originalQuestion);
      if (entry) {
        entry.question = action.payload.editedQuestion;
        entry.answer = action.payload.answer;
        entry.status = 'completed';
      }
    },
    
    // Utility action for debugging transcript deduplication
    getTranscriptStats: (state) => {
      const stats = {
        totalTranscripts: state.transcripts.length,
        customerTranscripts: state.transcripts.filter(t => t.speaker === 'customer').length,
        salespersonTranscripts: state.transcripts.filter(t => t.speaker === 'salesperson').length,
        recentTranscripts: state.transcripts.slice(-5).map(t => ({
          speaker: t.speaker,
          text: t.text.slice(0, 30) + '...',
          timestamp: t.timestamp
        }))
      };
      console.log('📊 Transcript Stats:', stats);
    },
    
    setSessionSaved: (state, action: PayloadAction<boolean>) => {
      state.sessionSaved = action.payload;
    },
    
    markSessionAsSaved: (state) => {
      state.sessionSaved = true;
    },
    
    resetSessionSavedStatus: (state) => {
      state.sessionSaved = false;
    },

    setIsSalespersonHost: (state, action: PayloadAction<boolean>) => {
      state.isSalespersonHost = action.payload;
    },

    // Upsert the multi-party roster (from participants_update). Marks the
    // salesperson as resolved when the roster contains one.
    setParticipants: (state, action: PayloadAction<Participant[]>) => {
      const incoming = action.payload || [];
      const byId = new Map(state.participants.map((p) => [p.participantId, p]));
      for (const p of incoming) {
        if (!p?.participantId) continue;
        const existing = byId.get(p.participantId);
        const merged = { ...existing, ...p };
        // Don't let a stale re-emit (e.g. from a peer task that hasn't received
        // the roster sync yet) revert a locally-confirmed name/role.
        if (state.confirmedParticipantIds.includes(p.participantId) && existing) {
          if (existing.name) merged.name = existing.name;
          if (existing.role) merged.role = existing.role;
        }
        byId.set(p.participantId, merged);
      }
      state.participants = Array.from(byId.values());
      if (state.participants.some((p) => p.role === 'salesperson')) {
        state.salespersonResolved = true;
      }
    },

    // Prompt the user to pick the rep from the live roster.
    setSalespersonUnresolved: (state, action: PayloadAction<Participant[]>) => {
      if (action.payload?.length) {
        const byId = new Map(state.participants.map((p) => [p.participantId, p]));
        for (const p of action.payload) {
          if (p?.participantId) byId.set(p.participantId, { ...byId.get(p.participantId), ...p });
        }
        state.participants = Array.from(byId.values());
      }
      state.salespersonResolved = false;
    },

    // Salesperson confirmed roles + names in the popup. Optimistically applies the
    // assignments to the local roster and marks those participantIds confirmed so
    // the popup does not immediately re-open for them (it re-opens only when a NEW
    // unconfirmed participant joins). Enforces exactly one salesperson locally.
    confirmParticipants: (
      state,
      action: PayloadAction<Array<{ participantId: string; role: 'customer' | 'salesperson'; name?: string }>>
    ) => {
      const assignments = action.payload || [];
      const byId = new Map(state.participants.map((p) => [p.participantId, p]));
      const salesId = assignments.find((a) => a.role === 'salesperson')?.participantId;
      for (const a of assignments) {
        if (!a?.participantId) continue;
        const prev = byId.get(a.participantId) || { participantId: a.participantId, role: a.role };
        byId.set(a.participantId, { ...prev, role: a.role, name: a.name ?? prev.name });
      }
      // Exactly one salesperson locally.
      if (salesId) {
        Array.from(byId.entries()).forEach(([id, p]) => {
          if (id !== salesId && p.role === 'salesperson') byId.set(id, { ...p, role: 'customer' });
        });
      }
      state.participants = Array.from(byId.values());
      state.salespersonResolved = state.participants.some((p) => p.role === 'salesperson');
      const ids = new Set(state.confirmedParticipantIds);
      assignments.forEach((a) => a?.participantId && ids.add(a.participantId));
      state.confirmedParticipantIds = Array.from(ids);
    },

    // Dismissing the popup without changes still acknowledges the current roster
    // so it doesn't immediately re-open (best-guess roles stay in effect).
    acknowledgeParticipants: (state) => {
      const ids = new Set(state.confirmedParticipantIds);
      state.participants.forEach((p) => p?.participantId && ids.add(p.participantId));
      state.confirmedParticipantIds = Array.from(ids);
    },

    updateEntryFeedback: (state, action: PayloadAction<{ id: string; feedback: EntryFeedback }>) => {
      const entry = state.entries.find(e => e.id === action.payload.id);
      if (entry) {
        entry.feedback = action.payload.feedback;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSpeechRateSettings.fulfilled, (state, action) => {
        if (action.payload) {
          state.speechRateSettings = {
            enabled: action.payload.enabled,
            time_window: action.payload.time_window,
            max_words: action.payload.max_words,
          };
        }
      })
      .addCase(fetchSpeechRateSettings.rejected, (state, action) => {
        console.error('Failed to fetch speech rate settings:', action.error);
        // Keep default settings on error
      });
  },
});

export const {
  addQuestion,
  addStoredAnswer,
  addAnswer,
  markAnswerError,
  updateCurrentSpeechRate,
  updateSpeechRateAnalysis,
  addSpeechRateAlert,
  clearSpeechRateAlert,
  addTranscript,
  clearConversation,
  markEntryAsOld,
  updateQuestion,
  getTranscriptStats,
  setSessionSaved,
  markSessionAsSaved,
  resetSessionSavedStatus,
  setParticipants,
  setSalespersonUnresolved,
  confirmParticipants,
  acknowledgeParticipants,
  setIsSalespersonHost,
  updateEntryFeedback,
} = conversationSlice.actions;

export default conversationSlice.reducer;